// filepath: /C:/Users/JakeB/Documents/commanderthing/frontend/src/components/CommanderInput.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { fetchCardByName, scryfallFetch } from '../utils/scryfallApi';
import { sanitizeCommanderName } from '../utils/sanitizeName';
import '../styles/CommanderInput.css';

// Scryfall SVG mana symbol URLs
const getManaSymbolUrl = (color) => {
  return `https://svgs.scryfall.io/card-symbols/${color}.svg`;
};

const CommanderInput = ({ onSubmit, commanders = [] }) => {
  const [commander, setCommander] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestVersionRef = useRef(0);

  // Fetch commanders from Scryfall with debouncing
  const fetchCommanderSuggestions = useCallback(async (query) => {
    if (query.length < 4) {
      setSuggestions([]);
      return;
    }

    // Increment version to track this request
    const thisRequest = ++requestVersionRef.current;
    
    setIsLoading(true);
    try {
      const data = await scryfallFetch(
        `https://api.scryfall.com/cards/search?q=is:commander+name:${encodeURIComponent(query)}&order=edhrec`
      );
      
      // Ignore if a newer request was made
      if (thisRequest !== requestVersionRef.current) {
        return;
      }
      
      if (data.data && !data._error) {
        // Map to objects with more details
        const commanderData = data.data
          .slice(0, 8)
          .map(card => {
            const keywords = card.keywords || [];
            const oracleText = card.oracle_text || '';
            // Detect partner type
            let partnerType = null;
            if (keywords.includes('Partner with') || oracleText.match(/Partner with \w/)) {
              partnerType = 'partner_with';
            } else if (oracleText.includes('Friends forever')) {
              partnerType = 'friends_forever';
            } else if (keywords.includes('Choose a background')) {
              partnerType = 'choose_a_background';
            } else if (keywords.includes('Partner') && !keywords.includes('Partner with')) {
              partnerType = 'partner';
            }
            
            return {
              name: card.name,
              image: card.image_uris?.art_crop || card.card_faces?.[0]?.image_uris?.art_crop || '',
              colors: card.color_identity || [],
              manaCost: card.mana_cost || card.card_faces?.[0]?.mana_cost || '',
              typeLine: card.type_line || '',
              partnerType: partnerType
            };
          });
        setSuggestions(commanderData);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      // Ignore errors from stale requests
      if (thisRequest !== requestVersionRef.current) {
        return;
      }
      console.error('Error fetching commander suggestions:', err);
      setSuggestions([]);
    } finally {
      if (thisRequest === requestVersionRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't submit if empty
    if (!commander.trim()) {
      return;
    }
    
    // If suggestions are showing and user hasn't selected one, use the first/highlighted suggestion
    let commanderToSubmit = commander;
    let previewToUse = selectedPreview;
    
    if (suggestions.length > 0) {
      // Use highlighted suggestion if available, otherwise use first suggestion
      const suggestionIndex = highlighted >= 0 ? highlighted : 0;
      const suggestion = suggestions[suggestionIndex];
      if (suggestion) {
        commanderToSubmit = suggestion.name;
        previewToUse = suggestion;
      }
    } else {
      // No suggestions loaded yet - do a quick search to find the closest match
      try {
        const data = await fetchCardByName(commander.trim());
        if (data && !data._error && data.name) {
          // Scryfall fuzzy match found a card - use it
          commanderToSubmit = data.name;
          previewToUse = {
            name: data.name,
            colors: data.color_identity || [],
            image: data.image_uris?.art_crop || data.card_faces?.[0]?.image_uris?.art_crop || ''
          };
        } else {
          // No match found - don't submit
          console.log('No commander match found for:', commander);
          return;
        }
      } catch (error) {
        console.error('Error finding commander:', error);
        return;
      }
    }
    
    // For double-faced cards, only use the front face name (before " // ")
    const frontFaceName = commanderToSubmit.split(' // ')[0];
    // Format commander name using the shared sanitizer
    const formattedCommander = sanitizeCommanderName(frontFaceName);
    
    // Use preview colors if available
    let colors = (previewToUse?.colors && previewToUse.colors.length > 0) ? previewToUse.colors : [];
    
    // Pass the color identity along with the commander
    onSubmit(formattedCommander, frontFaceName, colors);
    setShowSuggestions(false);
    setSelectedPreview(null);
    setCommander(commanderToSubmit); // Update input to show the selected commander
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setCommander(value);
    setHighlighted(-1);
    setSelectedPreview(null);
    
    // Clear previous debounce timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (value.length >= 4) {
      // Debounce the API call by 500ms
      debounceRef.current = setTimeout(() => {
        fetchCommanderSuggestions(value);
      }, 500);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSuggestionClick = (suggestion) => {
    setCommander(suggestion.name);
    setSelectedPreview(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current.blur();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setHighlighted((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      setHighlighted((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      if (suggestions[highlighted]) {
        setCommander(suggestions[highlighted].name);
        setSelectedPreview(suggestions[highlighted]);
        setSuggestions([]);
        setShowSuggestions(false);
        e.preventDefault();
      }
    }
  };

  // Render color identity pips using Scryfall SVGs
  const renderColorIdentity = (colors) => {
    if (!colors || colors.length === 0) {
      return (
        <img 
          src={getManaSymbolUrl('C')} 
          alt="Colorless" 
          className="mana-symbol"
          title="Colorless"
        />
      );
    }
    return colors.map((color, idx) => (
      <img 
        key={idx}
        src={getManaSymbolUrl(color)} 
        alt={color} 
        className="mana-symbol"
        title={color === 'W' ? 'White' : color === 'U' ? 'Blue' : color === 'B' ? 'Black' : color === 'R' ? 'Red' : color === 'G' ? 'Green' : color}
      />
    ));
  };

  return (
    <div className="commander-input-container">
      <form onSubmit={handleSubmit} autoComplete="off" className="commander-form">
        <div className="input-wrapper">
          <input
            type="text"
            value={commander}
            onChange={handleChange}
            onFocus={() => commander.length >= 2 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            onKeyDown={handleKeyDown}
            placeholder="Search for a commander..."
            required
            ref={inputRef}
            className="commander-search-input"
          />
          {isLoading && <span className="input-spinner"></span>}
        </div>
        
        {showSuggestions && suggestions.length > 0 && (
          <ul className="commander-autocomplete-list">
            {suggestions.map((suggestion, idx) => (
              <li
                key={suggestion.name}
                className={idx === highlighted ? 'highlighted' : ''}
                onMouseDown={() => handleSuggestionClick(suggestion)}
              >
                <div className="suggestion-image">
                  {suggestion.image ? (
                    <img src={suggestion.image} alt="" />
                  ) : (
                    <div className="image-placeholder">🎴</div>
                  )}
                </div>
                <div className="suggestion-info">
                  <span className="suggestion-name">{suggestion.name}</span>
                  <span className="suggestion-type">{suggestion.typeLine}</span>
                  {suggestion.partnerType && (
                    <span className="suggestion-partner-hint">
                      {suggestion.partnerType === 'partner' && '⚔️ Partner — choose a partner next'}
                      {suggestion.partnerType === 'partner_with' && '⚔️ Partner — has a specific partner'}
                      {suggestion.partnerType === 'friends_forever' && '🤝 Friends Forever — choose a partner next'}
                      {suggestion.partnerType === 'choose_a_background' && '📜 Choose a Background next'}
                    </span>
                  )}
                </div>
                <div className="suggestion-colors">
                  {renderColorIdentity(suggestion.colors)}
                </div>
              </li>
            ))}
          </ul>
        )}
        
        <button type="submit" className="build-button">
          <span className="button-icon">⚡</span>
          Build!
        </button>
      </form>

      {/* Selected Commander Preview */}
      {selectedPreview && (
        <div className="commander-preview">
          <div className="preview-image">
            {selectedPreview.image && (
              <img src={selectedPreview.image} alt={selectedPreview.name} />
            )}
          </div>
          <div className="preview-info">
            <h3>{selectedPreview.name}</h3>
            <div className="preview-colors">
              {renderColorIdentity(selectedPreview.colors)}
            </div>
            <p className="preview-type">{selectedPreview.typeLine}</p>
            {selectedPreview.partnerType && (
              <p className="preview-partner-hint">
                {selectedPreview.partnerType === 'partner' && '⚔️ You can choose a partner commander next'}
                {selectedPreview.partnerType === 'partner_with' && '⚔️ Their partner will be added automatically'}
                {selectedPreview.partnerType === 'friends_forever' && '🤝 You can choose a partner next'}
                {selectedPreview.partnerType === 'choose_a_background' && '📜 You can choose a Background next'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommanderInput;